import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LeftPanel from '../components/LeftPanel';
import PrimaryButton from '../components/PrimaryButton';
import ConfirmSubmissionModal from '../components/ConfirmSubmissionModal';
import InfoModal from '../components/InfoModal';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../components/UnsavedChangesModal';

function countWords(s) {
    if (!s) return 0;
    return s.trim().split(/\s+/).filter(Boolean).length;
}

function limitToWords(s, max) {
    if (!s) return '';
    const tokens = s.split(/(\s+)/); // keep whitespace tokens
    let wordCount = 0;
    const out = [];
    for (let i = 0; i < tokens.length; i++) {
        const t = tokens[i];
        if (/^\s+$/.test(t)) {
            // whitespace token: include only if we've already included some content
            if (out.length > 0) out.push(t);
            else out.push(t); // allow leading whitespace
        } else {
            if (t.trim() === '') {
                out.push(t);
                continue;
            }
            if (wordCount < max) {
                out.push(t);
                wordCount++;
            } else {
                break;
            }
        }
    }
    // join and return preserving whitespace (do not strip trailing spaces)
    return out.join('');
}

function CIGForm() {
    const [questions, setQuestions] = useState([]);
    const questionCount = questions.length;
    const [answers, setAnswers] = useState(() => new Array(0).fill(''));
    const [schoolName, setSchoolName] = useState('');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [saved, setSaved] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [fieldErrors, setFieldErrors] = useState({});
    const navigate = useNavigate();

    const loadData = async (countOverride, questionMetas) => {
        setLoading(true);
        const cnt = typeof countOverride === 'number' ? countOverride : questionCount;
        const metas = Array.isArray(questionMetas) ? questionMetas : questions;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/public/cig-applications', { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (res.data && res.data.exists) {
                // if already submitted, redirect to grants page
                if (res.data.completed) {
                    navigate('/grantlanding');
                    return;
                }
                setSaved(true);
                const next = new Array(cnt).fill('');
                for (let i = 0; i < cnt; i++) {
                    const key = (metas && metas[i] && metas[i].dbField) ? metas[i].dbField : `cig_question_${i + 1}`;
                    next[i] = res.data[key] || '';
                }
                setAnswers(next);
                if (res.data.schoolName) setSchoolName(res.data.schoolName);
                if (res.data.last_updated_by_first_name || res.data.updated_at || res.data.last_saved_at) {
                    setLastUpdated({
                        firstName: res.data.last_updated_by_first_name,
                        lastName: res.data.last_updated_by_last_name,
                        email: res.data.last_updated_by_email,
                        updatedAt: res.data.updated_at || res.data.last_saved_at
                    });
                }
                // record snapshot to detect unsaved changes
                if (initialSnapshot.current) initialSnapshot.current = { answers: JSON.stringify(next), schoolName: res.data.schoolName || '' };
            } else {
                setSaved(false);
                if (initialSnapshot.current) initialSnapshot.current = { answers: JSON.stringify(new Array(cnt).fill('')), schoolName: '' };
            }
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    };

    // fetch server-side question labels and then load saved data
    useEffect(() => {
        let mounted = true;
        axios.get('/api/public/questions/cig').then(res => {
            if (!mounted) return;
            const arr = Array.isArray(res.data) ? res.data : [];
            const merged = arr.map((meta, idx) => ({
                label: meta.label || '',
                type: meta.type || 'text',
                required: meta.required !== undefined ? meta.required : true,
                dbField: meta.dbField || `cig_question_${idx + 1}`
            }));
            setQuestions(merged);
            setAnswers(new Array(merged.length).fill(''));
            loadData(merged.length, merged);
        }).catch(() => {
            loadData(0);
        });
        return () => { mounted = false; };
    }, []);

    // fetch authenticated user info so we can show school name immediately
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const me = await api.get('/api/public/me');
                const name = me?.data?.schoolName || me?.data?.school_name || '';
                if (mounted && name) setSchoolName(name);
            } catch (e) {
                // ignore
            }
        })();
        return () => { mounted = false; };
    }, []);

    const initialSnapshot = useRef({ answers: JSON.stringify([]), schoolName: '' });

    const isDirty = () => {
        try {
            if (!initialSnapshot.current) return false;
            if (saving) return false;
            if (JSON.stringify(answers) !== initialSnapshot.current.answers) return true;
            if ((schoolName || '') !== (initialSnapshot.current.schoolName || '')) return true;
            return false;
        } catch (e) { return false; }
    };

    const pendingResolve = useRef(null);
    const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
    const showPrompt = () => new Promise((resolve) => {
        pendingResolve.current = resolve;
        setUnsavedModalOpen(true);
    });
    const handleModalClose = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(false); pendingResolve.current = null; }
    };
    const handleModalConfirm = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(true); pendingResolve.current = null; }
    };

    useUnsavedChangesWarning(isDirty, 'You have unsaved changes on this CIG form. Save draft or cancel to continue.', showPrompt);

    const formatToEastern = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            const datePart = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
            const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/New_York' });
            return `${datePart} - ${timePart} Eastern`;
        } catch (e) { return iso; }
    };

    const firstMissingIndex = () => {
        for (let i = 0; i < answers.length; i++) {
            if (!answers[i] || String(answers[i]).trim().length === 0) return i;
        }
        return -1;
    };

    const isLong = (i) => questions[i] && questions[i].type === 'long';

    const errorSx = {
        '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'error.main' } },
        '& .MuiFormLabel-root': { color: 'error.main' }
    };
    const requiredAsteriskSx = { '& .MuiFormLabel-asterisk': { color: 'warning.main' } };
    const fieldSx = (qName, base = {}) => ({
        ...base,
        ...(fieldErrors && fieldErrors[qName] ? errorSx : {})
    });

    const handleChange = (index, value) => {
        const limited = limitToWords(value, 200);
        const copy = [...answers];
        copy[index] = limited;
        setAnswers(copy);
        // clear any existing error for this question
        setFieldErrors(fe => ({ ...fe, [`q_${index}`]: undefined }));
    };

    const saveDraft = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {};
            for (let i = 0; i < questionCount; i++) {
                const key = (questions && questions[i] && questions[i].dbField) ? questions[i].dbField : `cig_question_${i + 1}`;
                payload[key] = answers[i];
            }
            payload.completed = false;
            await axios.post('/api/public/cig-applications', payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            setSaved(true);
            setFieldErrors({});
            await loadData();
            // update snapshot to current saved state
            initialSnapshot.current = { answers: JSON.stringify(answers), schoolName: schoolName || '' };
            setSnackbarMessage('Draft saved');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            // notify other components (ProgressSteps) to refresh status
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
        } catch (err) {
            console.error(err);
            setSnackbarMessage('Failed to save draft');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally { setSaving(false); }
    };

    const submitApplication = async () => {
        // validate all required questions and collect missing ones
        const missing = [];
        for (let i = 0; i < answers.length; i++) {
            const req = questions[i] && questions[i].required !== false;
            if (req && (!answers[i] || String(answers[i]).trim().length === 0)) missing.push(i);
        }
        if (missing.length > 0) {
            const errs = {};
            missing.forEach(i => { errs[`q_${i}`] = 'Required'; });
            setFieldErrors(errs);
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            try { const el = document.querySelector(`[name="q_${missing[0]}"]`); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus({ preventScroll: true }); } } catch (e) { }
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {};
            for (let i = 0; i < questionCount; i++) {
                const key = (questions && questions[i] && questions[i].dbField) ? questions[i].dbField : `cig_question_${i + 1}`;
                payload[key] = answers[i];
            }
            payload.completed = true;
            await axios.post('/api/public/cig-applications', payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            setSaved(true);
            // update snapshot to current saved state
            initialSnapshot.current = { answers: JSON.stringify(answers), schoolName: schoolName || '' };
            setSnackbarMessage('Application submitted');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            // notify other components (ProgressSteps) to refresh status before redirect
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
            // wait briefly so the success snackbar is visible before navigating away
            try {
                await new Promise(resolve => setTimeout(resolve, 800));
            } catch (e) {
                // ignore timer errors
            }
            navigate('/grantlanding');
        } catch (err) {
            console.error(err);
            setSnackbarMessage('Failed to submit application');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally { setSaving(false); }
    };

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoQuestion, setInfoQuestion] = useState('');

    const attemptSubmit = () => {
        // validate and show all missing
        const missing = [];
        for (let i = 0; i < answers.length; i++) {
            const req = questions[i] && questions[i].required !== false;
            if (req && (!answers[i] || String(answers[i]).trim().length === 0)) missing.push(i);
        }
        if (missing.length > 0) {
            const errs = {};
            missing.forEach(i => { errs[`q_${i}`] = 'Required'; });
            setFieldErrors(errs);
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            try { const el = document.querySelector(`[name="q_${missing[0]}"]`); if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus({ preventScroll: true }); } } catch (e) { }
            return;
        }
        setConfirmOpen(true);
    };

    const handleCloseSnackbar = () => setSnackbarOpen(false);

    return (
        <>
            <Box component="main">
                <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 0 }}>
                    <Grid item xs={12} md={3} lg={3}>
                        <LeftPanel items={["THE DEADLINE IS 11:59PM EST, JUNE 7, 2026.", "The selection process for this year's Retool Your School grants has been updated beginning in 2025. Schools will be evaluated based on the specific impact and feasibility of their proposed project(s), as well as their alignment with the program's core goals. We encourage you to provide detailed, thoughtful responses to clearly highlight the significance and necessity of your project(s).", "You can find the FAQs here."]}>
                            <PrimaryButton
                                onClick={() => {
                                    setInfoQuestion('What is the focus of the Campus Improvement Grant?');
                                    setInfoOpen(true);
                                }}
                                sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
                            >
                                FAQ
                            </PrimaryButton>
                            <InfoModal
                                open={infoOpen}
                                onClose={() => {
                                    setInfoOpen(false);
                                    setInfoQuestion('');
                                }}
                                openQuestion={infoQuestion}
                            />
                        </LeftPanel>
                    </Grid>
                    <Grid item xs={12} md={9} lg={9}>
                        <Paper elevation={1} sx={{ p: { xs: 0, md: 0 }, position: 'relative', mx: 'auto', maxWidth: { xs: '100%', md: '950px' } }}>
                            <Box sx={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, p: { xs: 3, md: 4 }, borderBottom: '1px solid #eee' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Campus Improvement Grant</Typography>
                                <Typography variant="subtitle1" sx={{ mb: 0 }}>School: {schoolName}</Typography>
                                {lastUpdated && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Last updated by {lastUpdated.firstName || ''} {lastUpdated.lastName || ''} ({lastUpdated.email || ''}) on {formatToEastern(lastUpdated.updatedAt)}
                                    </Typography>
                                )}
                                {/* note about required fields */}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, fontStyle: 'italic' }}>
                                    <span style={{ color: '#ff6600' }}>*</span> indicates a required field. Please complete all required items.
                                </Typography>
                            </Box>

                            <Box component="form" noValidate sx={{ maxHeight: '72vh', overflow: 'auto', p: { xs: 3, md: 5 } }}>
                                <Grid container spacing={2}>
                                    {questions.map((qObj, idx) => {
                                        const q = qObj.label;
                                        const val = answers[idx] || '';
                                        const words = isLong(idx) ? countWords(val) : countWords(val);
                                        const atLimit = isLong(idx) && words >= 200;
                                        return (
                                            <Grid item xs={12} md={12} key={idx}>
                                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                                    {idx + 1}. {q}{' '}
                                                    {qObj && qObj.required !== false && <span style={{ color: '#ff6600' }}>*</span>}
                                                </Typography>
                                                <TextField
                                                    name={`q_${idx}`}
                                                    value={val}
                                                    onChange={(e) => handleChange(idx, e.target.value)}
                                                    fullWidth
                                                    multiline={isLong(idx)}
                                                    minRows={isLong(idx) ? 3 : 1}
                                                    maxRows={isLong(idx) ? 20 : undefined}
                                                    variant="outlined"
                                                    required={qObj.required !== false}
                                                    error={!!fieldErrors[`q_${idx}`] || atLimit}
                                                    sx={fieldSx(`q_${idx}`, { mb: 2 })}
                                                    helperText={fieldErrors[`q_${idx}`] ? fieldErrors[`q_${idx}`] : ''}
                                                />
                                                {isLong(idx) && (
                                                    <Typography variant="caption" color={atLimit ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                                                        {words} / 200 words{atLimit ? ' — word limit reached' : ''}
                                                    </Typography>
                                                )}
                                            </Grid>
                                        );
                                    })}

                                    <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                        <PrimaryButton onClick={saveDraft} disabled={saving || loading}>{'Save Draft'}</PrimaryButton>
                                        <PrimaryButton onClick={attemptSubmit} disabled={saving || loading}>{'Submit'}</PrimaryButton>
                                    </Grid>
                                </Grid>
                            </Box>
                            <ConfirmSubmissionModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={async () => { setConfirmOpen(false); await submitApplication(); }} />
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
            <UnsavedChangesModal
                open={unsavedModalOpen}
                onClose={handleModalClose}
                onConfirm={handleModalConfirm}
                message={"It looks like you have unsaved changes on your Campus Information Form. Click Cancel to return to your form, then scroll to the bottom and save your work or hit \"OK\" to lose your changes."}
            />
            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ zIndex: 2000, mt: '72px' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default CIGForm;
